/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'

import ActionToggle from 'live/components/ui/action-toggle'

import {
    changeActive,
    changeConditions,
    changeDescription,
    changeRuleType,
    changeSeverity,
    changeSound,
    changeSaveState,
    getAssetRules,
    restoreRule,
    saveRule,
    setGlobalAlert,
    toggleSelected,
    updateRule
} from '../../actions'
import {
    editorColumns as columns,
    HIGHEST_SEVERITY,
    SECOND_HIGHEST_SEVERITY,
    UPDATE_RULES_ASSET
} from '../../constants'
import { Column, Columns, ButtonIcon, RegularIcon } from '../styles'
import { AlarmSeverity, Grid, MustSave, Row, SaveOrCancel } from '../styles/card'
import {
    Conditions,
    Divider,
    RuleEditor,
    RuleOptions,
    RuleOptionTitle,
    RuleToggles,
    ToggleContainer
} from '../styles/editor'
import ChannelSelectorBase from './ChannelSelector'
import ConditionsEditor from './ConditionsEditor'
import ComparatorSelectorBase from './ComparatorSelector'
import Description from './Description'
import Options from './Options'
import RigStateSelector from './RigStateSelector'
import RuleTypeSelector from './RuleTypeSelector'
import SeveritySelector from './SeveritySelector'
import ThresholdInputBase from './ThresholdInput'

const Rule = ({
    rule,
    sideview,
    selectedUnitSet,

    canEditGlobal,
    channel,
    curves,
    rigStateOutputs,
    saveStates,
    selectedIndices,
    sendBroadcast,

    changeActive,
    changeConditions,
    changeDescription,
    changeRuleType,
    changeSeverity,
    changeSound,
    changeSaveState,
    getAssetRules,
    restoreRule,
    saveRule,
    setGlobalAlert,
    toggleSelected,
    updateRule
}) => {
    const ref = useRef(null)
    const [scrolled, setScrolled] = useState(false)

    const newRule = !!rule.new
    const selected = selectedIndices.has(rule.index)
    const [collapsed, toggleCollapsed] = useState(!sideview)

    const active = rule.active ?? true
    const sound = rule.sound ?? false

    const description = rule.description || ''
    const ruleType = rule.global ? 'global' : 'user'
    const severity = rule.severity || 'warning'

    const conditions = useMemo(
        () => rule.conditions.map(cond => (cond.rigState ? { ...cond, threshold: cond.rigStateValues } : cond)) || [{}],
        [rule.conditions]
    )

    const color =
        rule.severity === HIGHEST_SEVERITY
            ? '#C43535'
            : rule.severity === SECOND_HIGHEST_SEVERITY
            ? '#F89300'
            : '#52BE80'
    const ruleOptionsColumns = [
        { flex: 4, gridArea: 'description', flexDirection: 'column', alignItems: 'flex-start', fontSize: 13 },
        { flex: 2, gridArea: 'severity', flexDirection: 'column', alignItems: 'flex-start', fontSize: 13 },
        { flex: 1, gridArea: 'rule-type', flexDirection: 'column', alignItems: 'flex-start', fontSize: 13 }
    ]

    const saveState = saveStates[rule.index]
    const editable = !rule.global || canEditGlobal
    const includesRuleType = newRule && canEditGlobal
    const mustSave = newRule || (saveState && saveState !== 'success')

    const ChannelSelector = props => (
        <ChannelSelectorBase
            {...props}
            curves={curves}
            first={sideview && channel}
            sideview={sideview}
            disabled={!editable}
            onChange={(option, prev) => {
                const toRigState = option === 'rigstate' && prev !== 'rigstate'
                const fromRigState = prev === 'rigstate' && option !== 'rigstate'

                if (toRigState || fromRigState) {
                    changeConditions(
                        rule.index,
                        conditions.map(({ threshold, ...cond }, idx) => ({
                            ...cond,
                            ...(idx === props.rowIndex
                                ? {
                                      channel: option,
                                      comparator: '',
                                      threshold: '',
                                      rigState: toRigState
                                  }
                                : { threshold })
                        }))
                    )
                } else {
                    props.onChange({ target: { value: option } })
                }
            }}
        />
    )
    const ComparatorSelector = props => (
        <ComparatorSelectorBase
            {...props}
            disabled={!editable}
            sideview={sideview}
            onChange={(option, changeThreshold, toRange) => {
                if (changeThreshold) {
                    changeConditions(
                        rule.index,
                        conditions.map(({ threshold, ...cond }, idx) => ({
                            ...cond,
                            ...(idx === props.rowIndex
                                ? {
                                      comparator: option,
                                      threshold: toRange
                                          ? Array.isArray(threshold)
                                              ? threshold
                                              : threshold === ''
                                              ? ['', '']
                                              : [threshold, threshold + 1]
                                          : Array.isArray(threshold)
                                          ? threshold[0]
                                          : threshold
                                  }
                                : { threshold })
                        }))
                    )
                } else {
                    props.onChange({ target: { value: option } })
                }
            }}
        />
    )
    const ThresholdInput = props =>
        props.state.channel === 'rigstate' ? (
            <RigStateSelector outputs={rigStateOutputs} {...props} />
        ) : (
            <ThresholdInputBase {...props} unitSet={selectedUnitSet} curves={curves} disabled={!editable} />
        )

    useEffect(() => {
        if (ref.current && newRule && !scrolled) {
            ref.current.scrollIntoView()
            setScrolled(false)
        }
    }, [newRule, scrolled])

    return (
        <>
            <Row collapsed={collapsed} mustSave={mustSave} ref={ref} inactive={!rule.active}>
                <Columns>
                    <Column style={columns[0]}>
                        <input type={'checkbox'} onChange={() => toggleSelected([rule.index])} checked={selected} />
                    </Column>
                    <Column style={columns[1]} inactive={!rule.active}>
                        <Description
                            collapsed={collapsed}
                            conditions={conditions}
                            curves={curves}
                            description={rule.description}
                            mustSave={mustSave}
                            newRule={newRule}
                            index={rule.index}
                            selectedUnitSet={selectedUnitSet}
                            sideview={sideview}
                            rigStateOutputs={rigStateOutputs}
                        />
                    </Column>
                    <Column style={columns[2]} inactive={!rule.active}>
                        <RegularIcon className={`far fa-${rule.active ? 'bell' : 'bell-slash'}`} />
                    </Column>
                    <Column style={columns[3]} inactive={!rule.active}>
                        <RegularIcon className={`far fa-${rule.global ? 'globe' : 'user'}`} />
                    </Column>
                    <Column style={columns[4]} inactive={!rule.active}>
                        <RegularIcon className={`far fa-volume${rule.sound ? '' : '-mute'}`} />
                    </Column>
                    <Column style={columns[5]} inactive={!rule.active}>
                        <AlarmSeverity>
                            <div>
                                <RegularIcon
                                    color={color}
                                    className={`fas fa-${rule.severity === 'normal' ? 'check' : 'exclamation'}-circle`}
                                />
                                <div>{rule.severity}</div>
                            </div>
                        </AlarmSeverity>
                    </Column>
                    <Column style={columns[6]}>
                        <Options rule={rule} sideview={sideview} />
                    </Column>
                    <Column style={columns[7]}>
                        <ButtonIcon
                            fontWeight={900}
                            className={`far fa-chevron-${collapsed ? 'down' : 'up'}`}
                            onClick={() => toggleCollapsed(c => !c)}
                        />
                    </Column>
                </Columns>
            </Row>

            {!collapsed && (
                <RuleEditor mustSave={mustSave} inactive={!rule.active}>
                    <RuleToggles>
                        <ToggleContainer active={active}>
                            <RegularIcon fontSize={14} className={`far fa-bell${active ? '' : '-slash'}`} />
                            <span>Active</span>
                            <ActionToggle
                                className="centered"
                                active={active}
                                disabled={!editable}
                                onChange={() => changeActive(rule.index, !rule.active)}
                            />
                        </ToggleContainer>
                        <ToggleContainer active={sound}>
                            <RegularIcon fontSize={14} className={`far fa-volume${sound ? '' : '-mute'}`} />
                            <span>Sound</span>
                            <ActionToggle
                                className="centered"
                                active={sound}
                                disabled={!editable}
                                onChange={() => changeSound(rule.index, !rule.sound)}
                            />
                        </ToggleContainer>
                    </RuleToggles>
                    <Divider />

                    <RuleOptions>
                        <Grid includesRuleType={includesRuleType} sideview={sideview}>
                            <Column style={ruleOptionsColumns[0]}>
                                <RuleOptionTitle>Description</RuleOptionTitle>
                                <input
                                    type="text"
                                    value={description}
                                    disabled={!editable}
                                    onChange={e => changeDescription(rule.index, e.target.value)}
                                />
                            </Column>
                            <Column style={ruleOptionsColumns[1]}>
                                <div style={{ width: '100%' }}>
                                    <RuleOptionTitle>Severity</RuleOptionTitle>
                                    <SeveritySelector
                                        severity={severity}
                                        setSeverity={severity => changeSeverity(rule.index, severity)}
                                        disabled={!editable}
                                    />
                                </div>
                            </Column>
                            {includesRuleType && (
                                <Column style={ruleOptionsColumns[2]}>
                                    <div style={{ width: '100%' }}>
                                        <RuleOptionTitle>Rule type</RuleOptionTitle>
                                        <RuleTypeSelector
                                            ruleType={ruleType}
                                            setRuleType={value => changeRuleType(rule.index, value === 'global')}
                                        />
                                    </div>
                                </Column>
                            )}
                        </Grid>
                    </RuleOptions>
                    <Divider />

                    <Conditions>
                        <ConditionsEditor
                            ChannelSelector={ChannelSelector}
                            ComparatorSelector={ComparatorSelector}
                            ThresholdInput={ThresholdInput}
                            conditions={conditions}
                            setConditions={conds =>
                                changeConditions(
                                    rule.index,
                                    conds.map(cond => ({
                                        ...cond,
                                        channel: cond.channel || (sideview && channel) || '',
                                        uom:
                                            selectedUnitSet?.data?.[cond.channel]?.unit ||
                                            curves?.[cond.channel]?.unit ||
                                            '',
                                        calculatedChannel: curves?.[cond.channel]?.calculated || false,
                                        ...(cond.channel === 'rigstate'
                                            ? { rigState: true, rigStateValues: cond.threshold }
                                            : { rigState: false })
                                    }))
                                )
                            }
                            disabled={!editable}
                        />
                        {editable && (
                            <>
                                <SaveOrCancel
                                    buttonMessage="Save"
                                    saving={saveState === 'saving'}
                                    saved={saveState === 'success'}
                                    mustSave={saveState === 'error'}
                                    disabled={
                                        !saveState ||
                                        saveState === 'success' ||
                                        saveState === 'error' ||
                                        conditions.some(
                                            cond =>
                                                !cond.channel ||
                                                !cond.comparator ||
                                                (Array.isArray(cond.threshold)
                                                    ? cond.threshold
                                                    : [cond.threshold]
                                                ).some(val => !val && val !== 0)
                                        )
                                    }
                                    disableCancel={!saveState || saveState === 'saving' || saveState === 'success'}
                                    hideCancel={newRule}
                                    onSubmit={() => {
                                        changeSaveState(rule.index, 'saving')
                                        saveRule(
                                            {
                                                ...rule,
                                                severity,
                                                description,
                                                active,
                                                conditions
                                            },
                                            (result, newAndModifiedRules) => {
                                                if (result === 'success') {
                                                    updateRule(rule.index, newAndModifiedRules[0], true)

                                                    if (sideview) {
                                                        getAssetRules(undefined, undefined, undefined, true)
                                                    } else {
                                                        sendBroadcast()
                                                    }
                                                } else {
                                                    changeSaveState(rule.index, result)
                                                    setGlobalAlert('Error saving rule', false)
                                                }
                                            },
                                            saveState === 'sound'
                                        )
                                    }}
                                    onCancel={() => {
                                        restoreRule(rule.index)
                                        if (!sideview) toggleCollapsed(true)
                                    }}
                                />
                                {mustSave && <MustSave>There are unsaved changes</MustSave>}
                            </>
                        )}
                    </Conditions>
                </RuleEditor>
            )}
        </>
    )
}

function mapStateToProps({
    editorReducer: {
        assetId,
        broadcastChannel,
        canEditGlobal,
        channel,
        curves,
        rigStateOutputs,
        saveState: saveStates,
        selected: selectedIndices
    }
}) {
    return {
        canEditGlobal,
        channel,
        curves,
        rigStateOutputs,
        saveStates,
        selectedIndices,
        sendBroadcast: () => broadcastChannel.postMessage({ type: UPDATE_RULES_ASSET, assetId })
    }
}

export default connect(mapStateToProps, {
    changeActive,
    changeConditions,
    changeDescription,
    changeRuleType,
    changeSeverity,
    changeSound,
    changeSaveState,
    getAssetRules,
    restoreRule,
    saveRule,
    setGlobalAlert,
    toggleSelected,
    updateRule
})(Rule)
